import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

const RESIZE_KEYBOARD_STEP_PX = 16;

interface UseResizeHandleOptions {
  minWidth: number;
  maxWidth: number;
  onWidthChange: (width: number) => void;
  initialWidth?: number;
  getCurrentWidth?: () => number;
  /**
   * Set when the handle sits on the panel’s start edge (e.g. left edge of a right-docked column).
   * Default `false` matches a trailing-edge handle: drag right → wider.
   */
  invertDelta?: boolean;
}

export const useResizeHandle = ({
  minWidth,
  maxWidth,
  onWidthChange,
  initialWidth,
  getCurrentWidth,
  invertDelta = false,
}: UseResizeHandleOptions) => {
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  const resizeMoveRafRef = useRef<number | null>(null);

  const getWidth = useCallback(() => {
    if (typeof getCurrentWidth === "function") return getCurrentWidth();
    if (typeof initialWidth === "number") return initialWidth;
    return 0;
  }, [getCurrentWidth, initialWidth]);

  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = getWidth();
    },
    [getWidth],
  );

  const handleMouseMove = useCallback(
    (e: globalThis.MouseEvent) => {
      if (!isResizing) return;

      if (resizeMoveRafRef.current !== null) {
        cancelAnimationFrame(resizeMoveRafRef.current);
      }
      resizeMoveRafRef.current = requestAnimationFrame(() => {
        resizeMoveRafRef.current = null;
        const delta = e.clientX - startXRef.current;
        const widthDelta = invertDelta ? -delta : delta;
        const newWidth = Math.min(
          Math.max(startWidthRef.current + widthDelta, minWidth),
          maxWidth,
        );
        onWidthChange(newWidth);
      });
    },
    [invertDelta, isResizing, maxWidth, minWidth, onWidthChange],
  );

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
    }
  }, [isResizing]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      const currentWidth = getWidth();

      if (event.key === "Home") {
        event.preventDefault();
        if (currentWidth !== minWidth) onWidthChange(minWidth);
        return;
      }

      if (event.key === "End") {
        event.preventDefault();
        if (currentWidth !== maxWidth) onWidthChange(maxWidth);
        return;
      }

      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }

      event.preventDefault();

      let deltaPx = 0;
      if (invertDelta) {
        deltaPx =
          event.key === "ArrowLeft"
            ? RESIZE_KEYBOARD_STEP_PX
            : -RESIZE_KEYBOARD_STEP_PX;
      } else {
        deltaPx =
          event.key === "ArrowRight"
            ? RESIZE_KEYBOARD_STEP_PX
            : -RESIZE_KEYBOARD_STEP_PX;
      }

      const nextWidth = Math.min(
        Math.max(currentWidth + deltaPx, minWidth),
        maxWidth,
      );
      if (nextWidth !== currentWidth) {
        onWidthChange(nextWidth);
      }
    },
    [getWidth, invertDelta, maxWidth, minWidth, onWidthChange],
  );

  useLayoutEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (resizeMoveRafRef.current !== null) {
        cancelAnimationFrame(resizeMoveRafRef.current);
        resizeMoveRafRef.current = null;
      }
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return {
    handleMouseDown,
    handleKeyDown,
    isResizing,
  };
};
